import { FunctionComponent, useMemo } from 'react';
import Calendar, { CalendarProps } from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import moment, { Moment } from 'moment-timezone';
import styles from './MultipleCalendarBooking.module.scss';
import clsx from 'clsx';

interface MultipleCalendarBookingProps {
    activeStartDate: Date | null;
    selectedDates: Array<Moment>;
    timeZone: string;
    onActiveStartDateChange: (date: Date) => void;
    onToggleDate: (value: Date) => void;
}

const toTzDayKey = (date: Date, timeZone: string) => {
    // Usamos el día "visible" del calendario pero en timezone del marketplace
    // para que coincida con los Moment que guardamos.
    const ymd = moment(date).format('YYYY-MM-DD');
    return moment.tz(ymd, 'YYYY-MM-DD', timeZone).format('YYYY-MM-DD');
};

export const MultipleCalendarBooking: FunctionComponent<MultipleCalendarBookingProps> = props => {
    const selectedDateStrings = useMemo(() => {
        // Normalizamos siempre al inicio del día en timezone del marketplace para evitar descalces
        // cuando `selectedDates` trae horas distintas o fue creado en otra TZ.
        return new Set(props.selectedDates.map(d => d.clone().tz(props.timeZone).startOf('day').format('YYYY-MM-DD')));
    }, [props.selectedDates, props.timeZone]);

    const tileClassName: CalendarProps['tileClassName'] = ({ date, view }) => {
        if (view !== 'month') return undefined;
        const key = toTzDayKey(date, props.timeZone);
        return clsx({
            [styles.tileSelected]: selectedDateStrings.has(key)
        });
    };

    return (
        <Calendar
            calendarType="US"
            onClickDay={(value: Date) => props.onToggleDate(value)}
            className={styles.calendar}
            nextLabel={<FontAwesomeIcon icon={faChevronRight} />}
            prevLabel={<FontAwesomeIcon icon={faChevronLeft} />}
            prev2Label={null}
            next2Label={null}
            showNeighboringMonth={false}
            locale={'en'}
            activeStartDate={props.activeStartDate ?? undefined}
            onActiveStartDateChange={({ activeStartDate }) => {
                if (activeStartDate) {
                    props.onActiveStartDateChange(activeStartDate);
                }
            }}
            tileClassName={tileClassName}
        />
    );
};

export default MultipleCalendarBooking;
